import { Button } from '@ophelos/ui/components/button';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupTextarea } from '@ophelos/ui/components/input-group';
import { MessageBubble } from '@ophelos/ui/components/message-bubble';
import { Popover, PopoverAnchor, PopoverContent } from '@ophelos/ui/components/popover';
import { Progress } from '@ophelos/ui/components/progress';
import { ArrowLeft, Info, Pencil } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { MOBILE_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import { useTranslation } from '@/hooks/useTranslation';
import {
  type BudgetQuestion,
  type BudgetStep,
  copyKeyFor,
  firstUnansweredStep,
  isGroupedStep,
  isMoneyStep,
  isAnsweredOnBehalf,
  needsReasking,
  questionsOf,
  selectedOptions,
  SITUATION_NONE,
  SITUATION_OPTION_GROUPS,
} from '@/lib/budget-assessment-questions';
import type { BudgetAnswerInput, BudgetAssessmentDto } from '@/lib/connect-api';
import { currencySymbol } from '@/lib/currency';
import { MultiSelectCombobox } from './MultiSelectCombobox';

interface ChatStepProps {
  assessment: BudgetAssessmentDto;
  /** The steps this assessment's tier asks, in order. */
  steps: readonly BudgetStep[];
  /** Writes one step's answers as a single unit of work. */
  onSaveAnswers: (entries: readonly (readonly [string, BudgetAnswerInput])[]) => Promise<void>;
  onComplete: () => void;
  /** Leaving the flow entirely — offered only where there is no earlier step to go back to. */
  onExit: () => void;
  isBusy: boolean;
}

/** One labelled amount in a group, with its hint revealed by focusing the field. */
function MoneyRow({
  labelId,
  hintId,
  label,
  hint,
  children,
}: {
  labelId: string;
  hintId: string;
  label: string;
  hint: string;
  children: ReactNode;
}) {
  const [hintOpen, setHintOpen] = useState(false);
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        <span id={labelId} className="text-sm text-foreground leading-snug">
          {label}
        </span>
        <HintPopover text={hint} id={hintId} open={hintOpen} onOpenChange={setHintOpen} />
      </div>
      {/* Focus/blur bubble from the input, which is the only keyboard route to the hint.
          Guarded on relatedTarget so it opens when the customer moves focus here and not
          when the step's first field is autofocused on arrival — programmatic focus comes
          from nowhere, so it carries none. */}
      <div
        className="w-28 shrink-0"
        onFocus={(e) => e.relatedTarget && setHintOpen(true)}
        onBlur={() => setHintOpen(false)}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * A field's guidance, one tap away.
 *
 * Popover rather than Tooltip because Radix tooltips deliberately never open on touch, and
 * this text carries the scope of the question — what counts as a priority debt, that a card
 * payment is what you pay rather than what you owe. Opening on hover as well as press gives
 * the same affordance on a mouse.
 *
 * Controlled, and `tabIndex={-1}`, so tabbing runs field to field rather than stopping at
 * every hint — the row opens it when its input takes focus instead. Without that a sighted
 * keyboard user has no route to it at all: `aria-describedby` serves assistive tech, and a
 * single-question step shows the hint as plain text, but a grouped row shows neither.
 */
function HintPopover({
  text,
  id,
  open,
  onOpenChange,
}: {
  text: string;
  id?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <button
          type="button"
          tabIndex={-1}
          data-ba-hint=""
          id={id}
          aria-label={text}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          onPointerEnter={(e) => e.pointerType === 'mouse' && onOpenChange(true)}
          onPointerLeave={(e) => e.pointerType === 'mouse' && onOpenChange(false)}
          onPointerUp={(e) => e.pointerType !== 'mouse' && onOpenChange(!open)}
        >
          <Info className="size-3.5" />
        </button>
      </PopoverAnchor>
      <PopoverContent
        side="top"
        align="start"
        className="w-auto max-w-64 px-3 py-2 text-xs text-muted-foreground"
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Without a Trigger, Radix counts the press that opened this as a click outside
        // and dismisses it on the same gesture. Any press on a hint control is ours.
        onPointerDownOutside={(e) => {
          if ((e.target as HTMLElement | null)?.closest?.('[data-ba-hint]')) e.preventDefault();
        }}
      >
        {text}
      </PopoverContent>
    </Popover>
  );
}

export function ChatStep({ assessment, steps, onSaveAnswers, onComplete, onExit, isBusy }: ChatStepProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const bottomRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  // Measured rather than a fixed value: the composer's height varies with the hint, and a
  // grouped step is several fields tall where a single question is one.
  const [composerHeight, setComposerHeight] = useState(0);

  const TOTAL = steps.length;
  const questions = useMemo(() => questionsOf(steps), [steps]);
  // Tier-level, and only ever about the wording of the counter: a tier whose steps each
  // hold one question counts questions, one that groups counts sections — "Question 4 of 6"
  // would be a lie when step 4 holds four of nineteen. Whether a given step *renders* as a
  // group is a property of that step, not of the tier: `full` groups its money questions
  // but still asks the two situation questions one at a time.
  const isGrouped = questions.length !== steps.length;

  const answersByKey = useMemo(
    () => new Map(assessment.answers.map((line) => [line.question_key, line])),
    [assessment.answers],
  );
  const answeredKeys = useMemo(() => assessment.answers.map((line) => line.question_key), [assessment.answers]);

  // -1 once every question is answered: the composer gives way to the finish CTA
  // rather than a step, which is the state a draft abandoned after the last answer
  // comes back in.
  // Everything answered is not the same as everything asked: a detail answered on the
  // customer's behalf has to reopen if they later flag a circumstance. Cleared once they
  // submit it, so a blank they chose to leave cannot reopen for ever.
  // State, not a ref: it is read during render, so writing it has to schedule one.
  const [answeredAgainst, setAnsweredAgainst] = useState<string | null>(null);
  /**
   * A written explanation whose circumstances have since been changed, so it now describes
   * something the customer no longer reports.
   *
   * Detected on the change rather than derived from the stored answers, because nothing
   * records which circumstances an explanation was written against — the pair is only
   * knowable while both are in hand. Deriving it instead by dropping `needsReasking`'s
   * "already written" guard would reopen the question on every load of a consistent
   * assessment, since this state resets on mount. Closing the reload gap needs the answer
   * to carry what it was written against, which is a server change (#7571).
   */
  const [detailStale, setDetailStale] = useState(false);
  const circumstancesNow = JSON.stringify(
    selectedOptions(answersByKey.get('situation_circumstances')?.answer_value).sort(),
  );
  const firstUnanswered = firstUnansweredStep(steps, answeredKeys);
  const reaskIndex =
    firstUnanswered !== -1 || answeredAgainst === circumstancesNow
      ? -1
      : steps.findIndex((step) =>
          step.questions.some(
            (q) => needsReasking(q.key, assessment.answers) || (detailStale && q.key === 'situation_detail'),
          ),
        );
  const nextIndex = firstUnanswered !== -1 ? firstUnanswered : reaskIndex;
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [draftOptions, setDraftOptions] = useState<string[]>([]);

  const activeIndex = editingStep ?? (nextIndex === -1 ? null : nextIndex);
  const activeStep = activeIndex == null ? null : (steps[activeIndex] ?? null);
  // Put to nobody: answered on the customer's behalf by the effect below, and left out of
  // the transcript because showing it as asked would misrepresent what happened. Only ever
  // a single-question step — a group has fields that must be asked.
  const skipActive =
    editingStep == null &&
    activeStep != null &&
    activeStep.questions.every((q) => isAnsweredOnBehalf(q.key, assessment.answers));

  const transcriptSteps = steps
    .map((step, index) => ({ step, index }))
    .slice(0, nextIndex === -1 ? TOTAL : nextIndex)
    .filter(({ step }) => !step.questions.every((q) => isAnsweredOnBehalf(q.key, assessment.answers)));

  const symbol = currencySymbol(assessment.currency);
  const answeredSteps = firstUnanswered === -1 ? TOTAL : firstUnanswered;
  // While editing, the count names the step being changed rather than the one they had
  // reached — the number they are looking at is the one they went back to.
  const position = editingStep != null ? editingStep + 1 : Math.min(answeredSteps + 1, TOTAL);
  const positionLabel = t(
    `budgetAssessment.${isGrouped ? 'progressSection' : 'progress'}${editingStep != null ? 'Editing' : ''}`,
    { current: position, total: TOTAL },
  );

  const questionLabel = (question: BudgetQuestion) => t(`budgetAssessment.questions.${copyKeyFor(question)}.label`);
  const fieldLabel = (question: BudgetQuestion) => t(`budgetAssessment.questions.${copyKeyFor(question)}.field`);
  const hint = (question: BudgetQuestion) => t(`budgetAssessment.questions.${copyKeyFor(question)}.hint`);
  /** What opens a step: a group's own intro, or the single question it asks. */
  const stepPrompt = (step: BudgetStep) =>
    step.sectionKey ? t(`budgetAssessment.sections.${step.sectionKey}`) : questionLabel(step.questions[0]);

  // Seed the composer whenever the active step changes. Prefilled from whatever is stored,
  // so a group reopened part-answered — by an edit, or by resuming a draft abandoned
  // mid-group — shows the figures already given beside the ones still missing.
  useEffect(() => {
    if (!activeStep) return;
    const seeded: Record<string, string> = {};
    for (const question of activeStep.questions) {
      const line = answersByKey.get(question.key);
      if (line?.amount_cents != null) seeded[question.key] = String(line.amount_cents / 100);
      else seeded[question.key] = typeof line?.answer_value === 'string' ? line.answer_value : '';
      if (question.input === 'multi_select') setDraftOptions(line ? selectedOptions(line.answer_value) : []);
    }
    setDrafts(seeded);
  }, [activeStep, answersByKey]);

  // A question that makes no sense to put to this customer is answered as "nothing to
  // add" instead. Guarded by a ref rather than the answer list, so a failed write does
  // not retry forever against a server that has already refused it.
  const answeredOnBehalf = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!skipActive || !activeStep || isBusy) return;
    const pending = activeStep.questions.filter((q) => !answeredOnBehalf.current.has(q.key));
    if (pending.length === 0) return;
    for (const question of pending) answeredOnBehalf.current.add(question.key);
    void onSaveAnswers(pending.map((question) => [question.key, { answer_value: '' }] as const));
  }, [skipActive, activeStep, isBusy, onSaveAnswers]);

  useEffect(() => {
    const composer = composerRef.current;
    if (!composer) return;
    const measure = () => setComposerHeight(composer.offsetHeight);
    measure();
    // Not available in the test environment, and the mount measurement is enough there.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    // Border box: the composer's home-indicator padding can change on its own.
    observer.observe(composer, { box: 'border-box' });
    return () => observer.disconnect();
  }, []);

  // The page itself is the scroller here — nothing in this tree has a bounded height,
  // and on desktop the route sits inside a ScrollArea besides. scrollIntoView is what
  // handles that, because it moves whichever ancestor actually scrolls; setting
  // scrollTop on a div that never overflows silently does nothing.
  useEffect(() => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, [answeredSteps, editingStep, skipActive]);

  // Desktop only: focusing on mobile raises the keyboard over the composer.
  useEffect(() => {
    if (!isMobile && activeStep?.questions[0]?.input === 'currency') firstInputRef.current?.focus();
  }, [isMobile, activeStep]);

  // Bounded, not just floored: a figure large enough to overflow (1e307 is accepted by the
  // number input) rounds to Infinity, which JSON-encodes to null and is refused as a
  // malformed amount rather than as the nonsense it is. Treated as nothing entered.
  const centsOf = (key: string) => {
    const cents = Math.round((Number.parseFloat(drafts[key] ?? '') || 0) * 100);
    return Number.isFinite(cents) ? Math.max(0, cents) : 0;
  };

  const answerFor = (question: BudgetQuestion): BudgetAnswerInput => {
    // A blank field is "nothing here", which is a captured zero (or empty string) — the
    // server refuses to complete an assessment with a missing line.
    if (question.input === 'currency') {
      const cents = centsOf(question.key);
      return { answer_value: cents, amount_cents: cents };
    }
    if (question.input === 'free_text') return { answer_value: (drafts[question.key] ?? '').trim() };
    return { answer_value: draftOptions };
  };

  const submit = async () => {
    if (!activeStep || isBusy) return;
    // Read before the write lands: once the answers come back, what they used to say is gone.
    const changedCircumstances =
      activeStep.questions.some((q) => q.key === 'situation_circumstances') &&
      JSON.stringify([...draftOptions].sort()) !== circumstancesNow;
    const explanation = answersByKey.get('situation_detail')?.answer_value;
    const hasWrittenExplanation = typeof explanation === 'string' && explanation.trim() !== '';

    await onSaveAnswers(activeStep.questions.map((question) => [question.key, answerFor(question)] as const));

    // Put the explanation back to the customer rather than letting it stand against a
    // circumstance they have just removed. It reopens prefilled, so nothing they wrote is
    // discarded — they can keep it, reword it, or clear it.
    if (changedCircumstances && hasWrittenExplanation) setDetailStale(true);
    if (activeStep.questions.some((q) => q.key === 'situation_detail')) {
      setAnsweredAgainst(circumstancesNow);
      setDetailStale(false);
    }
    setEditingStep(null);
  };

  /**
   * Back means the previous step, not the previous page — answers are already saved, so
   * stepping back is opening that one for editing. Pressing it repeatedly keeps walking
   * backwards; it is not a toggle, so the anchor is whichever step is on screen, edit or
   * not. Cancel is what returns to where they were. Once there is nothing further back,
   * it leaves the flow.
   */
  const stepBack = () => {
    const positions = transcriptSteps.map(({ index }) => index);
    const from = editingStep != null ? positions.indexOf(editingStep) : positions.length;
    const previous = from > 0 ? positions[from - 1] : null;
    if (previous != null) setEditingStep(previous);
    else onExit();
  };

  const optionLabels = (values: string[]) =>
    values
      .map((value) => {
        const option = SITUATION_OPTION_GROUPS.flatMap((g) => g.options).find((o) => o.value === value);
        return option ? t(`budgetAssessment.situation.options.${option.labelKey}`) : value;
      })
      .join(', ');

  // gap-0: CurrencyDisplay's default 0.5 gap sits between symbol, integer and cents,
  // reading as "€1,200 .00" at this size.
  const money = (major: number) => <CurrencyDisplay amount={major} currency={assessment.currency} className="gap-0" />;

  /**
   * What the customer sees back once a step is answered.
   *
   * A group answers with its total: four figures echoed one after another would bury the
   * one number the step was gathering, and the fields themselves are still one tap away
   * behind the edit control.
   */
  const stepSummary = (step: BudgetStep) => {
    if (isMoneyStep(step)) {
      const total = step.questions.reduce((sum, q) => sum + (answersByKey.get(q.key)?.amount_cents ?? 0), 0);
      return money(total / 100);
    }
    const [question] = step.questions;
    const line = answersByKey.get(question.key);
    if (!line) return null;
    if (question.input === 'free_text') {
      const written = typeof line.answer_value === 'string' ? line.answer_value.trim() : '';
      return written || t('budgetAssessment.situation.nothingAdded');
    }
    const chosen = selectedOptions(line.answer_value);
    return chosen.length === 0 ? t('budgetAssessment.situation.options.noneOfThese') : optionLabels(chosen);
  };

  const situationGroups = SITUATION_OPTION_GROUPS.map((group) => ({
    label: t(`budgetAssessment.situation.groups.${group.labelKey}`),
    options: group.options.map((option) => ({
      value: option.value,
      label: t(`budgetAssessment.situation.options.${option.labelKey}`),
    })),
  }));

  /**
   * What names a control to a screen reader.
   *
   * In a group the visible row label is the name, and the hint hangs off it as a
   * description. A lone question has no visible label — the message bubble above asked it —
   * so the question itself becomes the name. Pointing at the hint instead, as this did,
   * announces "Include ground rent and service charges" as the field's name and never says
   * what is being asked.
   */
  const naming = (question: BudgetQuestion, grouped: boolean) =>
    grouped
      ? { 'aria-labelledby': `ba-field-${question.key}`, 'aria-describedby': `ba-hint-${question.key}` }
      : { 'aria-label': questionLabel(question), 'aria-describedby': `ba-hint-${question.key}` };

  const amountInput = (question: BudgetQuestion, index: number, grouped = true) => (
    <InputGroup>
      {/* Leading on purpose: this marks what the field accepts, so it stays at
          the field's edge rather than following the locale's placement. */}
      <InputGroupAddon>{symbol}</InputGroupAddon>
      <InputGroupInput
        ref={index === 0 ? firstInputRef : undefined}
        type="number"
        inputMode="decimal"
        min="0"
        step="1"
        placeholder="0"
        value={drafts[question.key] ?? ''}
        disabled={isBusy}
        {...naming(question, grouped)}
        onChange={(e) => setDrafts((prev) => ({ ...prev, [question.key]: e.target.value }))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void submit();
          }
          if (e.key === 'Escape' && editingStep != null) setEditingStep(null);
        }}
      />
    </InputGroup>
  );

  /**
   * A group's fields, rendered in the transcript directly under the message that asks for
   * them rather than in the pinned composer.
   *
   * Four fields with their hints laid out vertically is most of a phone screen, which left
   * the question stranded at the top of an empty transcript with its answer pinned to the
   * bottom. One row per field puts the two back together, and the hint moves behind an
   * info control so it costs no height until asked for.
   */
  const groupedFields = (step: BudgetStep) => (
    <div className="rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
      {step.questions.map((question, index) => (
        <MoneyRow
          key={question.key}
          labelId={`ba-field-${question.key}`}
          hintId={`ba-hint-${question.key}`}
          label={fieldLabel(question)}
          hint={hint(question)}
        >
          {amountInput(question, index)}
        </MoneyRow>
      ))}
    </div>
  );

  /**
   * A step's inputs, which always sit in the transcript directly under the message asking
   * for them. A group gets its card of rows; a lone question gets its hint and one control.
   */
  const stepInputs = (step: BudgetStep) => {
    if (isGroupedStep(step)) return groupedFields(step);
    const [question] = step.questions;
    return (
      <div className="space-y-2">
        <p id={`ba-hint-${question.key}`} className="text-xs text-muted-foreground">
          {hint(question)}
        </p>
        {question.input === 'currency' ? (
          amountInput(question, 0, false)
        ) : question.input === 'free_text' ? (
          <InputGroup>
            <InputGroupTextarea
              rows={3}
              className="resize-none"
              value={drafts[question.key] ?? ''}
              disabled={isBusy}
              {...naming(question, false)}
              placeholder={t('budgetAssessment.situation.detailPlaceholder')}
              onChange={(e) => setDrafts((prev) => ({ ...prev, [question.key]: e.target.value }))}
              // No Enter-to-submit here: the customer is writing prose and needs the key
              // for line breaks.
              onKeyDown={(e) => {
                if (e.key === 'Escape' && editingStep != null) setEditingStep(null);
              }}
            />
          </InputGroup>
        ) : (
          // Escape is handled here rather than on the control: the combobox's own popover
          // consumes the key, so an edit could be cancelled from every field but this one.
          <div onKeyDown={(e) => e.key === 'Escape' && editingStep != null && setEditingStep(null)}>
            <MultiSelectCombobox
              groups={situationGroups}
              value={draftOptions}
              onChange={setDraftOptions}
              exclusiveOption={SITUATION_NONE}
              disabled={isBusy}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col flex-1 min-h-0">
      {/* Sticky so the customer can always see how far through they are, and get back,
          without scrolling to the top of a transcript that only grows. */}
      <div className="sticky top-0 z-10 w-full bg-background/95 backdrop-blur-lg border-b border-border/50">
        <div className="w-full max-w-sm mx-auto px-4 py-3 space-y-2">
          <button
            type="button"
            onClick={stepBack}
            disabled={isBusy}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" />
            {t('budgetAssessment.disclosure.back')}
          </button>
          <Progress value={(Math.min(answeredSteps, nextIndex === -1 ? TOTAL : nextIndex) / TOTAL) * 100} />
          <p className="text-xs text-muted-foreground">{positionLabel}</p>
        </div>
      </div>

      <div className="flex-1 w-full">
        <div className="w-full max-w-sm mx-auto px-4 py-6 space-y-4" aria-live="polite">
          {transcriptSteps.map(({ step, index }) => (
            <div key={index} className="space-y-2">
              <MessageBubble
                variant="received"
                showAvatar={false}
                showSenderName={false}
                showTimestamp={false}
                content={stepPrompt(step)}
              />
              {editingStep === index ? (
                stepInputs(step)
              ) : (
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingStep(index)}
                  disabled={isBusy}
                  aria-label={t('budgetAssessment.editAnswer', { question: stepPrompt(step) })}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Pencil className="size-3.5" />
                </button>
                {/* flex-1 gives the bubble a definite width to size against: its own
                    max-w-[85%] would otherwise resolve against a shrink-to-fit parent
                    sized by this very text, so the content overflows its rounded box. */}
                <MessageBubble
                  className="flex-1 min-w-0"
                  variant="sent"
                  showAvatar={false}
                  showSenderName={false}
                  showTimestamp={false}
                  content={stepSummary(step)}
                />
              </div>
              )}
            </div>
          ))}

          {activeStep && editingStep == null && !skipActive && (
            <div className="space-y-2">
              <MessageBubble
                variant="received"
                showAvatar={false}
                showSenderName={false}
                showTimestamp={false}
                content={stepPrompt(activeStep)}
              />
              {stepInputs(activeStep)}
            </div>
          )}

          {/* Scroll target for the newest message, and a spacer the height of the
              composer. scrollIntoView aligns this element's bottom with the bottom of
              the scrollport, which the pinned composer covers — without the height, it
              is the last message that ends up underneath it. On mobile the chat icon
              floats just above the composer (3.75rem: its 2.75rem plus the 1rem gap),
              so the spacer clears that too, or the icon sits on the active input. */}
          <div ref={bottomRef} style={{ height: isMobile ? `calc(${composerHeight}px + 3.75rem)` : composerHeight }} />
        </div>
      </div>

      <BottomActionBar ref={composerRef} variant="composer">
        <div className="w-full max-w-sm mx-auto px-4 space-y-3">
          {skipActive ? null : activeStep ? (
            <>
              <div className="flex gap-2">
                {editingStep != null && (
                  <Button
                    variant="outline"
                    size="lg"
                    className="flex-1"
                    onClick={() => setEditingStep(null)}
                    disabled={isBusy}
                  >
                    {t('budgetAssessment.cancel')}
                  </Button>
                )}
                <Button size="lg" className="flex-1" onClick={() => void submit()} disabled={isBusy}>
                  {editingStep != null ? t('budgetAssessment.save') : t('budgetAssessment.continue')}
                </Button>
              </div>
            </>
          ) : (
            <Button size="xl" className="w-full" onClick={onComplete} disabled={isBusy}>
              {t('budgetAssessment.continue')}
            </Button>
          )}
        </div>
      </BottomActionBar>
    </div>
  );
}
